import React, { useEffect, useState, useCallback, useRef } from "react";
import { X } from "lucide-react";

import { notificationAPI } from "../services/notificationAPI";
import { useAuth } from "../context/AuthContext";
import { formatDate } from "../utils/utils";

const NotificationPage = ({ isOpen, onClose, onNotificationCountChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { user } = useAuth();
  const socketRef = useRef(null);
  const audioRef = useRef(new Audio("/notification-sound.mp3")); // Default sound

  // Handle new notification from socket
  const handleNewNotification = useCallback((notification) => {
    setNotifications((prev) => [notification, ...prev]);

    // Play notification sound
    if (audioRef.current) {
      audioRef.current
        .play()
        .catch((err) =>
          console.error("Error playing notification sound:", err)
        );
    }
  }, []);

  // Handle notification count update
  const handleNotificationCount = useCallback(
    (data) => {
      if (onNotificationCountChange) {
        onNotificationCountChange(data.count);
      }
    },
    [onNotificationCountChange]
  );

  // Connect to socket when component mounts
  useEffect(() => {
    if (user?.id) {
      socketRef.current = notificationAPI.connectSocket(
        user.id,
        handleNewNotification,
        handleNotificationCount
      );
    }

    return () => {
      notificationAPI.disconnectSocket();
    };
  }, [user?.id, handleNewNotification, handleNotificationCount]);

  // Fetch notifications when opened
  useEffect(() => {
    if (isOpen) {
      fetchNotifications(1, true);
      markAllAsRead();
    }
  }, [isOpen]);

  const fetchNotifications = async (pageNum = 1, reset = false) => {
    if (loading || (!hasMore && !reset)) return;

    try {
      setLoading(true);
      const res = await notificationAPI.getNotifications(user.id, pageNum);

      const newNotifications = res.data.notifications || [];

      if (reset) {
        setNotifications(newNotifications);
      } else {
        setNotifications((prev) => [...prev, ...newNotifications]);
      }

      setPage(pageNum);
      setHasMore(newNotifications.length > 0 && pageNum < res.data.totalPages);

      // Update notification count in parent component
      if (onNotificationCountChange && res.data.unreadCount !== undefined) {
        onNotificationCountChange(res.data.unreadCount);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAllAsRead = async () => {
    if (!user?.id || !isOpen) return;

    try {
      await notificationAPI.markAllAsRead();

      // Update local state to mark all as read
      setNotifications((prev) =>
        prev.map((notification) => ({
          ...notification,
          isRead: true,
        }))
      );

      // Update notification count in parent component
      if (onNotificationCountChange) {
        onNotificationCountChange(0);
      }
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const getFilteredNotifications = () => {
    const all = Array.isArray(notifications) ? notifications : [];
    console.log('All notifications:', all.map(n => ({ type: n.notificationType, message: n.message })));
    console.log('Active filter:', activeFilter);
    
    if (activeFilter === "All") {
      return all;
    }
    
    const filtered = all?.filter(
      (notification) => notification.notificationType?.toLowerCase() === activeFilter.toLowerCase()
    );
    console.log('Filtered notifications:', filtered);
    return filtered;
  };

  const formatNotificationText = (text) => {
    if (!text) return "";
    const appNumberRegex = /(#\d+)/g;
    const parts = text.split(appNumberRegex);

    return parts.map((part, index) => {
      if (part.startsWith("#")) {
        return (
          <span key={index} className="font-bold text-white">
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5 && !loading && hasMore) {
      fetchNotifications(page + 1);
    }
  };

  return (
    <div
      className={`fixed top-14 right-4 z-30 w-80 sm:w-96 max-h-[90vh] bg-[#0f0f0f] rounded-md border border-[#333333] shadow-lg overflow-hidden ${
        !isOpen && "hidden"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-2 ">
        <h3 className="font-medium text-white text-sm">Notifications</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white focus:outline-none"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Filter buttons */}
      <div className="flex flex-wrap gap-1 px-3 py-2 bg-[#131313]">
        {["All", "Credit Manager", "System", "Announcement"].map((filter) => (
          <button
            key={filter}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveFilter(filter);
            }}
            className={`px-2 sm:px-3 py-1 rounded-3xl text-xs ring-1 whitespace-nowrap ${
              activeFilter === filter
                ? "bg-yellow-400/20 text-[#FFCA20] ring-[#FFCA20] font-medium"
                : "bg-[#131313] ring-[#333333] text-gray-300 hover:bg-gray-800"
            }`}
          >
            {filter}
          </button>
        ))}
      </div>

      {/* Notification list */}
      <div
        className="max-h-[70vh] overflow-y-auto divide-y divide-dashed divide-gray-700"
        onScroll={handleScroll}
      >
        {getFilteredNotifications().length > 0 ? (
          getFilteredNotifications().map((notification, index) => (
            <div
              key={notification._id || index}
              className={`flex items-start gap-2 sm:gap-3 ml-2 sm:ml-4 px-2 sm:px-4 py-3 hover:bg-[#1a1a1a] transition ${
                !notification.isRead ? "bg-[#1a1a1a]/30" : ""
              }`}
            >
              {/* Unread indicator */}
              {!notification.isRead && (
                <div className="w-2 h-2 rounded-full bg-[#FFCA20] mt-2"></div>
              )}

              {/* Content */}
              <div className="flex-1">
                <p className="text-sm text-white leading-snug">
                  {formatNotificationText(notification.message)}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {formatDate(notification.sentAt || notification.createdAt)}
                </p>
              </div>

              {/* Type badge */}
              <div className="ml-auto px-1 sm:px-2 py-1 rounded text-[9px] sm:text-[10px] bg-[#131313] text-gray-300 border border-[#333333] whitespace-nowrap">
                {notification.notificationType}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-gray-500 py-6 text-sm">
            No notifications yet
          </div>
        )}

        {loading && (
          <div className="text-center text-gray-500 py-4 text-sm">
            Loading...
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPage;
